import {
	faDistributeSpacingHorizontal,
	faRulerCombined,
	faXmark,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	Anchor,
	CircleButton,
	Switch,
	type SwitchProps,
	Tooltip,
	Typography,
	formatNumber,
} from "@tosize/ui";
import { type ReactNode, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useConfig } from "../../../../context/ConfigContext";
import { useCabinetStore } from "../../../../context/StoreContext";
import { AppModus, LayoutModus, useCanvasStore } from "../../../../hooks/useCanvasStore";
import { useNavigation } from "../../../../hooks/useNavigation";
import { Slider } from "../../../Slider";
import TitleBlock from "../../../TitleBlock";
import { BACKGROUND_THICKNESS } from "../../../renderer/data/Background";
import {
	type Side,
	canMergeWith,
	compartmentsOnSameX,
	compartmentsOnSameY,
	touchesEdges,
	updateCompartment,
} from "../../../renderer/data/Compartment";
import { Divider, DividerSmall } from "../../Divider";
import Navigation from "../Navigation";

export default function AllCompartmentsContent() {
	const intl = useIntl();
	const { navigate } = useNavigation();

	return (
		<div className="space-y-3">
			<HorizontalsAndVerticals />
			<Divider />
			<DivideCompartments />
			<Divider />
			<Background />

			<Navigation
				prev={{
					onClick: () => navigate(AppModus.form),
					title: intl.formatMessage({ defaultMessage: "Afmetingen en materialen", id: "9ghEyB" }),
				}}
				next={{
					onClick: () => navigate(AppModus.compartment),
					title: intl.formatMessage({ defaultMessage: "Vakopties wijzigen", id: "BP/PXd" }),
				}}
			/>
		</div>
	);
}

function HorizontalsAndVerticals() {
	const { state, api } = useCabinetStore();
	const intl = useIntl();

	const canMergeAny = useMemo(
		() =>
			Array.from(state.compartments.values()).some((compartment) =>
				Object.entries(touchesEdges(compartment, state))
					.filter(([, touches]) => !touches)
					.some(([side]) => canMergeWith(compartment, side as Side, state, api)),
			),
		[state, api],
	);

	return (
		<div className="flex flex-col gap-y-1.5">
			<TitleBlock
				title={intl.formatMessage({ defaultMessage: "Staanders en liggers", id: "GEAtSk" })}
				explanation={
					<div className="space-y-2">
						<Typography variant="paragraph">
							<FormattedMessage
								defaultMessage="Staanders en liggers zijn belangrijke componenten in de constructie van de kast. Staanders zijn de verticale (rechtopstaande) planken. Ze hebben de dikte die je in de vorige stap hebt gekozen. Liggers zijn de horizontale (liggende) planken die op de staanders worden geplaatst. De liggers rusten altijd op de staanders en hebben de dikte die je in de vorige stap hebt gekozen."
								id="GSPf3f"
							/>
						</Typography>
						<Typography variant="paragraph-bold">
							<FormattedMessage defaultMessage="Verplaatsen" id="Rb+aso" />
						</Typography>
						<Typography variant="paragraph">
							<FormattedMessage
								defaultMessage="Door staanders en liggers in de kast te verplaatsen maak je jouw ideale indeling. Nadat je deze optie hebt aangezet kan je staanders/en of liggers verplaatsen door erop te klikken en te slepen. Als je niet verder kan heb je de maximale vakgrootte bereikt. Door in het rechter menu op de afmetingen {logo} te klikken, kan je de exacte maten van de kast bekijken. Je ziet altijd de binnenmaten van de vakken."
								id="HVAA60"
								values={{ logo: <FontAwesomeIcon icon={faRulerCombined} /> }}
							/>
						</Typography>
						<Typography variant="paragraph-bold">
							<FormattedMessage defaultMessage="Maximale vakafmetingen open vak" id="NA6dlh" />
						</Typography>
						<Typography variant="paragraph">
							<FormattedMessage
								defaultMessage="Ontwerpen met een plankdikte tot en met 18 mm: De maximale breedte van een vak bij een plankdikte van 18 mm is 80,0 cm. De maximale hoogte van een vak is 157,4 cm."
								id="PimB9r"
							/>
						</Typography>
						<Typography variant="paragraph">
							<FormattedMessage
								defaultMessage="Ontwerpen met een plankdikte vanaf 25 mm: De maximale breedte of hoogte is 157,4 cm, de andere zijde is vervolgens maximaal 100,0 cm. Zo kan je of een hoog vak, of een breed vak samenstellen."
								id="krJvOu"
							/>
						</Typography>
						<Typography variant="paragraph-bold">
							<FormattedMessage defaultMessage="Verwijderen" id="mcAe/q" />
						</Typography>
						<Typography variant="paragraph">
							<FormattedMessage
								defaultMessage="Door staanders of liggers in de kast te verwijderen, kan je vakken samen voegen. Nadat je deze optie hebt aangezet kan je staanders of liggers met een {logo} verwijderen. Sommige kan je niet verwijderen omdat anders de vakken te groot worden."
								id="tFalqF"
								values={{
									logo: <FontAwesomeIcon icon={faXmark} />,
								}}
							/>
						</Typography>
						<Typography variant="paragraph-bold">
							<FormattedMessage defaultMessage="Liggers" id="CsZU/+" />
						</Typography>
						<Typography variant="paragraph">
							<FormattedMessage
								defaultMessage="Door liggers in de kast toe te voegen kan je extra vakken creëren. De minimale afstand tussen de liggers is 20 cm."
								id="bqun+8"
							/>
						</Typography>
						<Typography variant="paragraph">
							<FormattedMessage
								defaultMessage="In de volgende stap kun je ook legplanken van 18mm dikte toevoegen. Legplanken zijn de horizontale planken die binnen het raamwerk van de staanders en liggers worden geplaatst. Je kan er 1 of meerdere achter een deurtje plaatsen of in een open vak. Ze kunnen in hoogte versteld worden door middel van de rijboring. Dan zie je dus in dat vak de rijboring!"
								id="QeNhLg"
							/>
						</Typography>
						<Typography variant="paragraph-bold">
							<FormattedMessage defaultMessage="Staanders" id="QLKoWU" />
						</Typography>
						<Typography variant="paragraph">
							<FormattedMessage
								defaultMessage="Door staanders in de kast toe te voegen kan je extra vakken creëren. De minimale afstand tussen de staanders is 10 cm."
								id="F5ch+2"
							/>
						</Typography>
					</div>
				}
			/>

			<Item id={LayoutModus.move}>
				<FormattedMessage defaultMessage="Verplaatsen" id="Rb+aso" />
			</Item>

			<DividerSmall />

			<Item id={LayoutModus.merge} disabled={!canMergeAny}>
				<FormattedMessage defaultMessage="Verwijderen" id="mcAe/q" />
			</Item>

			<DividerSmall />

			<Item id={LayoutModus.addHorizontal}>
				<FormattedMessage defaultMessage="Ligger(s) toevoegen" id="SC53bc" />
			</Item>

			<DividerSmall />

			<Item id={LayoutModus.addVertical}>
				<FormattedMessage defaultMessage="Staander(s) toevoegen" id="9Yf4n0" />
			</Item>
		</div>
	);
}

function DivideCompartments() {
	const intl = useIntl();

	return (
		<div className="flex flex-col gap-y-1.5">
			<TitleBlock
				title={intl.formatMessage({ defaultMessage: "Vakken gelijk verdelen", id: "u9PBS9" })}
				explanation={intl.formatMessage(
					{
						defaultMessage:
							"Met deze optie kan je vakken eenvoudig gelijkmatig verdelen over de breedte of hoogte. Dit kan horizontaal (van links naar rechts) of verticaal (van boven naar beneden). Selecteer minimaal twee aaneengesloten vakken en verdeel deze vervolgens door op {icon} te klikken.",
						id: "oN/O5x",
					},
					{
						icon: (
							<FontAwesomeIcon icon={faDistributeSpacingHorizontal} className="text-primary-dark" />
						),
					},
				)}
			/>

			<Item id={LayoutModus.distributeHorizontal}>
				<FormattedMessage defaultMessage="Horizontaal" id="Imrf7E" />
			</Item>

			<DividerSmall />

			<Item id={LayoutModus.distributeVertical}>
				<FormattedMessage defaultMessage="Verticaal" id="rZTD62" />
			</Item>
		</div>
	);
}

function Background() {
	const intl = useIntl();
	const { routes } = useConfig();

	return (
		<div className="flex flex-col gap-y-1.5">
			<TitleBlock
				title={intl.formatMessage({ defaultMessage: "Achterwand (verplicht)", id: "CAoiVz" })}
				explanation={
					<>
						<Typography variant="paragraph">
							{intl.formatMessage(
								{
									defaultMessage:
										"De achterwanden zijn verplicht en worden per vak tussen de staanders en liggers geklemd. De achterwanden worden gemaakt van {thickness} dik materiaal. De achterwand zorgt voor veel stevigheid! Kies je voor een kast van {depth} diep? Dan is de effectieve diepte van een vak dus {value}.",
									id: "FDBAhj",
								},
								{
									thickness: formatNumber({
										lang: intl.locale,
										value: BACKGROUND_THICKNESS,
										unit: "millimeter",
									}),
									depth: formatNumber({ lang: intl.locale, value: 40, unit: "centimeter" }),
									value: formatNumber({ lang: intl.locale, value: 38.2, unit: "centimeter" }),
								},
							)}
						</Typography>
						<Typography variant="paragraph-bold">
							<FormattedMessage defaultMessage="Diepte aanpassen" id="a7YaAC" />
						</Typography>
						<Typography variant="paragraph">
							{intl.formatMessage({
								defaultMessage:
									"De achterwand kan per vak naar voren geplaatst worden. Wil je in een of meerder vakken en andere diepte? Selecteer het vak en pas deze eenvoudig via het formulier aan. Wanneer deze functie geactiveerd wordt, wordt er standaard 1 keer een achterwandversteviging meegeleverd in het montagepakket. Dit is een multiplexplaat van 70x50 cm die je kan gebruiken ter versteviging voor het ophangen van je TV.",
								id: "bfiV2i",
							})}{" "}
							{routes?.get(
								"inspiratie/alles-wat-je-moet-weten-over-de-achterwand-van-een-tosize-furniture-vakkenkast",
							) && (
								<Anchor
									className="inline"
									href={routes?.get(
										"inspiratie/alles-wat-je-moet-weten-over-de-achterwand-van-een-tosize-furniture-vakkenkast",
									)}
								>
									{intl.formatMessage({
										defaultMessage: "Meer informatie over deze functie vind je in dit artikel.",
										id: "QyIJel",
									})}
								</Anchor>
							)}
						</Typography>
						<Typography variant="paragraph-bold">
							<FormattedMessage defaultMessage="Achterwand weglaten?" id="Zg1lZ8" />
						</Typography>
						<Typography variant="paragraph">
							{intl.formatMessage({
								defaultMessage:
									"Dat kan enkel op eigen risico. Wij adviseren minimaal 1 achterwand per rij te monteren om de stevigheid te waarborgen. Ook is het goed om te beseffen dat je dus wel gaatjes ziet bij vakken waar de achterwand tijdens de montage wordt weggelaten. Als je voor MDF hebt gekozen, kun je deze gaatjes natuurlijk wel dicht plamuren voordat je gaat schilderen.",
								id: "3ABmUx",
							})}
						</Typography>
					</>
				}
			/>

			<div className="flex flex-col gap-2">
				<Item id={LayoutModus.background}>
					<FormattedMessage defaultMessage="Diepte aanpassen" id="a7YaAC" />
				</Item>

				<BackgroundItem />
			</div>
		</div>
	);
}

function BackgroundItem() {
	const { selectedCompartmentIds, layoutMode } = useCanvasStore();
	const { state, setStore } = useCabinetStore();

	const currentId = selectedCompartmentIds.at(0);

	const max = state.depth - BACKGROUND_THICKNESS;
	const min = 0;
	const step = 10;

	const value = useMemo(() => {
		const currentCompartment = currentId ? state.compartments.get(currentId) : undefined;
		const zValue = currentCompartment?.background?.z ?? 0;
		return max - zValue;
	}, [currentId, state.compartments, max]);

	const changeBackgroundPosition = (newValue: number) => {
		let transformedValue = max - newValue;

		if (transformedValue < min || transformedValue < step) {
			transformedValue = min;
		}

		if (transformedValue > max || transformedValue > max - step) {
			transformedValue = max;
		}

		if (newValue === max - step) {
			transformedValue = max - Math.floor((max - step) / step) * step;
		}

		return setStore((store) => {
			const compartments = new Map(state.compartments);
			const compartment = currentId ? store.compartments.get(currentId) : undefined;

			if (compartment) {
				compartments.set(
					compartment.id,
					updateCompartment(
						{ ...compartment, background: { ...compartment, z: transformedValue } },
						state.thickness,
					),
				);
			}

			return { ...state, compartments };
		});
	};

	if (layoutMode !== LayoutModus.background || selectedCompartmentIds.length === 0) {
		return null;
	}

	return (
		<Slider
			shouldTransformMMToCM
			name="background"
			min={min}
			max={max}
			step={step}
			value={[value]}
			onValueChange={([v]) => (v !== undefined ? changeBackgroundPosition(v) : undefined)}
			onValueDown={() => changeBackgroundPosition(value - step)}
			onValueUp={() => changeBackgroundPosition(value + step)}
		/>
	);
}

interface ItemProps extends SwitchProps {
	id: LayoutModus;
	children: ReactNode;
	tooltip?: ReactNode;
}

function Item({ id, children, ...props }: ItemProps) {
	const { state } = useCabinetStore();
	const {
		layoutMode,
		setLayoutMode,
		selectedCompartmentIds,
		deselectCompartment,
		deselectAllCompartments,
	} = useCanvasStore();
	const compartments = selectedCompartmentIds.map((id) => state.compartments.get(id)!);

	const selectedCompartmentsNotOnSameY = compartments.filter(
		(c) => compartments[0] && !compartmentsOnSameY(c, compartments[0]),
	);

	const selectedCompartmentsNotOnSameX = compartments.filter(
		(c) => compartments[0] && !compartmentsOnSameX(c, compartments[0]),
	);

	return (
		<Switch
			className="justify-between"
			labelPosition="left"
			id={id}
			checked={layoutMode === id}
			onCheckedChange={() => {
				setLayoutMode(layoutMode === id ? null : id);

				if (layoutMode === id) {
					deselectAllCompartments();
				}

				if (layoutMode === LayoutModus.distributeHorizontal) {
					selectedCompartmentsNotOnSameX.map((c) => deselectCompartment(c));
				}

				if (layoutMode === LayoutModus.distributeVertical) {
					selectedCompartmentsNotOnSameY.map((c) => deselectCompartment(c));
				}
			}}
			{...props}
		>
			{children}

			{!!props.tooltip && (
				<Tooltip content={props.tooltip} color="shades-light">
					<CircleButton component="button" className="ml-2 text-sm" />
				</Tooltip>
			)}
		</Switch>
	);
}
